/* eslint-disable max-len */
import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';

import {Helmet} from 'react-helmet';
import {Auth} from 'aws-amplify';
import '@tides/yorha-react/dist/main.css';

import * as UserActions from '../store/actions/user';
import * as VideoActions from '../store/actions/video';

import api, {graphqlQueryBuild} from '../services/api';
import {debugCNitoUrl, useBrand, useGetUserToken} from '../helpers';
import {notWebMediaQuery} from './../styles';

import PlayerError from '../components/PlayerError';
import Button from '../components/Button';
import Loader from '../components/Loader';
import PurePlayer, {configureStallIndicator} from '../components/PurePlayer';
import {analyticsTrackState} from '../helpers/Analytics';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 1rem 4rem;

    ${notWebMediaQuery} {
        margin-top: 78px;
        padding: 0 20px;
    }
`;

const VideoPlayerContainer = styled.div`
    width: 100%;
    height: 100%;
    margin-bottom: 2rem;
    position: relative;

    .bitmovinplayer-container {
        width: 100% !important;
    }

    #yui-wrapper {
        max-height: calc(95vh - 80px);
    }
`;

export const LivePlayer = props => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const {
        params: {live_id},
    } = useRouteMatch();
    const user = useSelector(state => state.user);
    const config = useSelector(state => state.config);
    const userToken = useGetUserToken();
    const brand = useBrand();

    const [decodedToken, setDecodedToken] = useState(undefined);

    const idp = user && user.idp;

    const [tokenRefreshed, setTokenRefreshed] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [adStarted, setAdStarted] = useState(false);
    const [startedHeartBeat, setStartedHearBeat] = useState(false);
    const [availableLives, setAvailableLives] = useState(undefined);

    const [currentLive, setCurrentLive] = useState(undefined);
    const [contentSrc, setContentSrc] = useState(undefined);
    const [playerError, setPlayerError] = useState(undefined);

    props = {
        ...props,
        ...useSelector(state => {
            return {
                ...state.episode,
                ...state.show,
                ...state.video,
            };
        }),
    };

    useEffect(() => {
        analyticsTrackState('now', {
            section: 'LiveTV',
            subsection: 'nvs',
            contenttype: 'LiveTV',
            contenttitle: 'LiveTV',
        });

        loadLives();

        return () => {
            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Content_Completed',
                data: {
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });
        };

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (user && user.jwtToken) {
            setDecodedToken(JSON.parse(atob(user.jwtToken.split('.')[1])));
        }
    }, [user]);

    useEffect(() => {
        if (decodedToken) {
            let now = Date.now() / 1000;

            if (decodedToken.exp > now) {
                setTokenRefreshed(true);
            } else {
                Auth.currentAuthenticatedUser()
                    .then(res => {
                        dispatch(
                            UserActions.login({
                                jwtToken:
                                    res.signInUserSession.idToken.jwtToken,
                            }),
                        );
                    })
                    .catch(err => {
                        // eslint-disable-next-line
                        console.error(err);
                    });
            }
        }

        // eslint-disable-next-line
    }, [decodedToken]);

    useEffect(() => {
        return () => {
            if (userToken !== undefined && live_id) {
                let content_stop_url = `${(config.catalog_settings &&
                    config.catalog_settings.endpoint) ||
                    'https://avs-proxy.dev.aaa.wmgp.io/v1'}/R/${
                    user.catalogLanguage
                }/${
                    config.catalog_settings && config.catalog_settings.platforms
                        ? config.catalog_settings.platforms[0]
                        : 'PCTV_DASH'
                }/${user.catalogTenant ||
                    'CNPLUS_LATAM_LATAM'}/CONTENT/STOP?contentId=${live_id}&asJson=Y&type=VOD&bookmark=0&deltaThreshold=0`;

                let headers = new Headers();
                headers.append('Authorization', `Bearer ${userToken}`);

                fetch(content_stop_url, {headers}).then(res => res.json());
            }
        };

        // eslint-disable-next-line
    }, [userToken, live_id, window.location.pathname]);

    useEffect(() => {
        if (tokenRefreshed) {
            preparePlayback();
        }

        // eslint-disable-next-line
    }, [tokenRefreshed, user, dispatch]);

    useEffect(() => {
        if (props.videos && props.videos[live_id]) {
            setContentSrc(props.videos[live_id].src);
        }

        // eslint-disable-next-line
    }, [props.videos]);

    useEffect(() => {
        if (availableLives) {
            let currentLive = availableLives.find(
                item => item.id.toString() === live_id,
            );

            if (currentLive) {
                setCurrentLive(currentLive);
                currentLive.url && setContentSrc(currentLive.url);
            } else {
                history.replace(`/now${debugCNitoUrl()}`);
            }
        }

        // eslint-disable-next-line
    }, [availableLives]);

    useEffect(() => {
        if (playerReady) {
            sendPlayerReadyAnalyticsCall();

            configureStallIndicator(brand);
        }

        // eslint-disable-next-line
    }, [playerReady]);

    useEffect(() => {
        if (playerReady && adStarted) {
            sendPlayerReadyAnalyticsCall();

            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Ad_Started',
                data: {
                    ad_id: 'pre-roll-1' /* ad identifier */,
                    ad_duration: 0 /* ad duration in seconds */,
                    ad_type:
                        'preroll' /* ad type (e.g. preroll, midroll, postroll) */,
                    platform: 'web',
                    content_name: currentLive && currentLive.title,
                    content_id: live_id,
                    content_type: 'live',

                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    logintype: user.user_type,
                    brand:
                        brand === 'cnapp'
                            ? 'cartoonnetwork'
                            : `cartoon network|${brand}`,
                },
            });
        }

        // eslint-disable-next-line
    }, [playerReady, adStarted]);

    const loadLives = async () => {
        let query = graphqlQueryBuild(
            'live',
            `
                id
                title
                description
                image
                url
                needFilter
            `,
        );

        let live_response = await api
            .get('', {
                params: {
                    query,
                },
            })
            .then(res => res)
            .catch(err => err.response);

        if (live_response.data.data && live_response.data.data.live) {
            let lives = live_response.data.data.live;

            setAvailableLives([...lives]);
        } else {
            setAvailableLives([]);
        }
    };

    const preparePlayback = async () => {
        if (
            live_id.indexOf('freelive') >= 0 ||
            live_id.indexOf('toontubers') >= 0
        )
            return;

        let playback_response = await dispatch(
            VideoActions.preparePlayback(live_id, user.jwtToken),
        );

        if (playback_response.resultCode !== 'OK') {
            if (playback_response.errorDescription === 'FRUITION_EXCEED') {
                dispatch({
                    type: 'MODAL_SHOW',
                    modal: {
                        visible: true,
                        title: t('video.concurrencyError'),
                        buttons: (
                            <>
                                <Button
                                    margin="0 1rem 0 auto"
                                    onClick={() =>
                                        dispatch({type: 'MODAL_CLOSE'})
                                    }
                                >
                                    Ok
                                </Button>
                            </>
                        ),
                    },
                });

                setPlayerError(t('video.concurrencyError'));
            } else {
                setContentSrc(null);
                setPlayerError(t('onnow.geoblockError'));
            }
        }

        return playback_response;
    };

    const memoizedPlayerComponent = React.useMemo(() => {
        if (contentSrc === undefined && !playerError) {
            return <Loader />;
        } else {
            return (
                <>
                    {playerError && <PlayerError error_message={playerError} />}

                    {!playerError && (
                        <PurePlayer
                            content={{...currentLive, src: contentSrc}}
                            setPlayerReady={setPlayerReady}
                            setAdStarted={setAdStarted}
                        />
                    )}
                </>
            );
        }

        // eslint-disable-next-line
    }, [contentSrc, playerReady, playerError, currentLive, startedHeartBeat]);

    const sendPlayerReadyAnalyticsCall = () => {
        if (!startedHeartBeat) {
            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Player_Ready',
                data: {
                    playerid: '',
                    content_duration: '',
                    content_dataCreated: '' /* YYYY-MM-DD (e.g. 2018-08-30) */,
                    content_dataAired: '' /* YYYY-MM-DD (e.g. 2018-08-30) */,
                    content_name: currentLive && currentLive.title,
                    content_id: live_id,
                    content_type: 'live',
                    content_showName: '' /* show name */,
                    content_seasonNumber:
                        '' /* season number (e.g. 1, 2, 3, etc.) */,
                    content_episodeNumber:
                        '' /* episode number (e.g. 1, 2, 3, etc.) */,
                    content_genre: '' /* genre (e.g. drama, comedy, etc.) */,
                    content_rating:
                        '' /* rating (e.g. tvy, tvg, tvpg, tvm, etc.) */,
                    content_originator:
                        '' /* originator (e.g. warnermedia, sony, disney, etc.) */,
                    content_network:
                        '' /* network (e.g. fox, brabo, espn, etc.) */,
                    content_mvpd:
                        '' /* mvpd (e.g. comcast, directv, dish, etc.) */,
                    content_authorized: '' /* true or false */,
                    day_part:
                        '' /* time of day (e.g. morning, daytime, primetime, etc.) */,
                    content_feed:
                        '' /* feed (e.g. east-hd, west-hd, east-sd, etc.) */,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    brand:
                        brand === 'cnapp'
                            ? 'cartoonnetwork'
                            : `cartoon network|${brand}`,
                },
            });

            setStartedHearBeat(true);
        }
    };

    return (
        <>
            {live_id.toString().indexOf('freelive') >= 0 ||
            live_id.toString().indexOf('toontubers') >= 0 ? (
                <>
                    <Helmet>
                        <title>{t('onnow.seoLiveTitle')}</title>
                        <meta
                            name="description"
                            content={t('onnow.seoLiveDescription')}
                        />

                        <link
                            rel="alternate"
                            href={`https://cnapp.cartoonnetwork.com.br/now/${live_id}`}
                            hreflang="pt-br"
                        />
                        <link
                            rel="alternate"
                            href={`https://cnapp.cartoonnetwork.com.ar/now/${live_id}`}
                            hreflang="es-ar"
                        />
                        <link
                            rel="alternate"
                            href={`https://cnapp.cartoonnetwork.com.co/now/${live_id}`}
                            hreflang="es-co"
                        />
                        <link
                            rel="alternate"
                            href={`https://cnapp.cartoonnetworkla.com/now/${live_id}`}
                            hreflang="es-us"
                        />
                        <link
                            rel="alternate"
                            href={`https://cnapp.cartoonnetwork.com.mx/now/${live_id}`}
                            hreflang="es-mx"
                        />

                        <script type="application/ld+json">
                            {`{
                                "@context": "https://schema.org",
                                "@type": "VideoObject",
                                "name": "${t('onnow.seoLiveTitle').replace(
                                    /\n/g,
                                    ' ',
                                )}",
                                "description": "${t(
                                    'onnow.seoLiveDescription',
                                ).replace(/\n/g, ' ')}",
                                "thumbnailUrl":
                                    "https://cn.i.cdn.ti-platform.com/cnplus/images/live_pp_2x.png",
                                "uploadDate": "2021-06-02"
                                ${
                                    contentSrc
                                        ? `, "contentUrl": "${contentSrc}"`
                                        : ''
                                }
                            }`}
                        </script>
                    </Helmet>
                </>
            ) : (
                <Helmet>
                    <link
                        rel="alternate"
                        href={`https://cnapp.cartoonnetwork.com.br/now/${live_id}`}
                        hreflang="pt-br"
                    />
                    <link
                        rel="alternate"
                        href={`https://cnapp.cartoonnetwork.com.ar/now/${live_id}`}
                        hreflang="es-ar"
                    />
                    <link
                        rel="alternate"
                        href={`https://cnapp.cartoonnetwork.com.co/now/${live_id}`}
                        hreflang="es-co"
                    />
                    <link
                        rel="alternate"
                        href={`https://cnapp.cartoonnetworkla.com/now/${live_id}`}
                        hreflang="es-us"
                    />
                    <link
                        rel="alternate"
                        href={`https://cnapp.cartoonnetwork.com.mx/now/${live_id}`}
                        hreflang="es-mx"
                    />
                </Helmet>
            )}
            <Container>
                <VideoPlayerContainer>
                    {memoizedPlayerComponent}
                </VideoPlayerContainer>
            </Container>
        </>
    );
};

export default LivePlayer;
